import { useState } from "react";
import type { Lesson, ScenarioResult } from "@/lib/types";
import { ScenarioView } from "./ScenarioView";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Circle, ArrowLeft, Trophy, Star } from "lucide-react";

interface LessonViewProps {
	lesson: Lesson;
	onBack: () => void;
	onComplete: (results: ScenarioResult[]) => void;
	previousResults: ScenarioResult[];
}

export function LessonView({ lesson, onBack, onComplete, previousResults }: LessonViewProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [results, setResults] = useState<ScenarioResult[]>(previousResults);
	const [showConcepts, setShowConcepts] = useState(previousResults.length === 0);
	const [showSummary, setShowSummary] = useState(false);

	const progress = (results.length / lesson.scenarios.length) * 100;

	const handleScenarioComplete = (correct: boolean, attempts: number) => {
		const scenario = lesson.scenarios[currentIndex];
		if (!scenario) return;
		const existing = results.find((r) => r.scenarioId === scenario.id);
		if (!existing) {
			setResults([...results, { scenarioId: scenario.id, correct, attempts }]);
		}
	};

	const handleNext = () => {
		if (currentIndex < lesson.scenarios.length - 1) {
			setCurrentIndex(currentIndex + 1);
		} else {
			setShowSummary(true);
			onComplete(results);
		}
	};

	const handleStartPractice = () => {
		setShowConcepts(false);
	};

	if (showSummary) {
		const correctCount = results.filter((r) => r.correct).length;
		const totalAttempts = results.reduce((sum, r) => sum + r.attempts, 0);
		const perfectScore = correctCount === lesson.scenarios.length && totalAttempts === lesson.scenarios.length;

		return (
			<div className="max-w-lg mx-auto space-y-6">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="w-4 h-4 mr-1" />
					Back to Lessons
				</Button>

				<Card>
					<CardHeader className="text-center pb-2">
						<div className="mx-auto mb-2">
							{perfectScore ? (
								<Trophy className="w-12 h-12 text-yellow-500" />
							) : correctCount >= lesson.scenarios.length / 2 ? (
								<Star className="w-12 h-12 text-blue-500" />
							) : (
								<Circle className="w-12 h-12 text-muted-foreground" />
							)}
						</div>
						<CardTitle>
							{perfectScore
								? "Perfect Score!"
								: correctCount >= lesson.scenarios.length / 2
									? "Good Work!"
									: "Keep Practicing!"}
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="text-center space-y-1">
							<p className="text-2xl font-bold">
								{correctCount} / {lesson.scenarios.length}
							</p>
							<p className="text-sm text-muted-foreground">scenarios solved correctly on first try</p>
						</div>

						<div className="space-y-2">
							{lesson.scenarios.map((s, i) => {
								const result = results.find((r) => r.scenarioId === s.id);
								return (
									<div key={s.id} className="flex items-center gap-2 text-sm">
										{result?.correct ? (
											<CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
										) : (
											<Circle className="w-4 h-4 text-muted-foreground shrink-0" />
										)}
										<span>{s.title}</span>
										{result && (
											<Badge variant="outline" className="ml-auto text-xs">
												{result.attempts} {result.attempts === 1 ? "attempt" : "attempts"}
											</Badge>
										)}
									</div>
								);
							})}
						</div>

						<Button className="w-full" onClick={onBack}>
							Back to Lessons
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (showConcepts) {
		return (
			<div className="max-w-lg mx-auto space-y-4">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="w-4 h-4 mr-1" />
					Back to Lessons
				</Button>

				<Card>
					<CardHeader>
						<CardTitle className="text-xl">{lesson.title}</CardTitle>
						<p className="text-sm text-muted-foreground">{lesson.description}</p>
					</CardHeader>
					<CardContent className="space-y-4">
						<div>
							<h4 className="text-sm font-semibold mb-2">Key Concepts</h4>
							<ul className="space-y-2">
								{lesson.concepts.map((c, i) => (
									<li key={i} className="text-sm flex gap-2">
										<span className="text-primary font-bold shrink-0">{i + 1}.</span>
										<span>{c}</span>
									</li>
								))}
							</ul>
						</div>

						<Button className="w-full" onClick={handleStartPractice}>
							Start Practice ({lesson.scenarios.length} scenarios)
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	const currentScenario = lesson.scenarios[currentIndex];
	if (!currentScenario) return null;

	return (
		<div className="max-w-lg mx-auto space-y-4">
			{/* Header */}
			<div className="flex items-center gap-2">
				<Button variant="ghost" size="sm" onClick={onBack}>
					<ArrowLeft className="w-4 h-4 mr-1" />
					Back
				</Button>
				<span className="text-sm font-medium truncate">{lesson.title}</span>
			</div>

			{/* Progress */}
			<div className="space-y-1">
				<div className="flex justify-between text-xs text-muted-foreground">
					<span>Progress</span>
					<span>{results.length} / {lesson.scenarios.length} completed</span>
				</div>
				<Progress value={progress} className="h-2" />
			</div>

			{/* Scenario dots */}
			<div className="flex gap-1.5 justify-center">
				{lesson.scenarios.map((s, i) => {
					const result = results.find((r) => r.scenarioId === s.id);
					const isCurrent = i === currentIndex;
					return (
						<button
							key={s.id}
							onClick={() => setCurrentIndex(i)}
							className={`w-6 h-6 rounded-full text-xs font-medium transition-all flex items-center justify-center ${
								isCurrent
									? "bg-primary text-primary-foreground ring-2 ring-ring ring-offset-2 ring-offset-background"
									: result?.correct
										? "bg-green-500 text-white"
										: result
											? "bg-orange-400 text-white"
											: "bg-muted text-muted-foreground"
							}`}
						>
							{i + 1}
						</button>
					);
				})}
			</div>

			{/* Current scenario */}
			<ScenarioView
				key={currentScenario.id}
				scenario={currentScenario}
				scenarioNumber={currentIndex + 1}
				totalScenarios={lesson.scenarios.length}
				onComplete={handleScenarioComplete}
				onNext={handleNext}
				isLast={currentIndex === lesson.scenarios.length - 1}
			/>
		</div>
	);
}
